/**
 * Tiny markdown formatter shared across Holly UI components.
 *
 * Holly returns messages with light markdown — `**bold**`, `_italic_`, and
 * newlines — plus occasional inline `<a href="...">label</a>` CTAs (e.g. the
 * "Complete registration" deep link). We HTML-encode the source text first
 * (to prevent XSS from the upstream API) and then re-introduce the small set
 * of inline tags we want: <strong>, <em>, <br /> and a hardened <a>.
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function formatHollyMarkdown(text: string): string {
  let html = escapeHtml(text);

  // Re-hydrate <a> tags from the upstream payload with a whitelisted attribute
  // set. Only http/https hrefs survive — javascript:, data:, etc. are dropped
  // and the link is rendered as plain text.
  html = html.replace(
    /&lt;a\b([\s\S]*?)&gt;([\s\S]*?)&lt;\/a&gt;/gi,
    (_match, rawAttrs: string, label: string) => {
      const hrefMatch = /href=&quot;([\s\S]*?)&quot;/i.exec(rawAttrs);
      if (!hrefMatch) return label;
      const href = hrefMatch[1];
      if (!/^https?:\/\//i.test(href)) return label;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="aiLink">${label}</a>`;
    }
  );

  return html
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/\n/g, '<br />');
}
